import { useEffect, useState } from 'react';

export interface Countdown {
  /** Whole seconds left, never below zero. */
  secondsRemaining: number;
  expired: boolean;
}

/**
 * A wall clock for a timed block, anchored to a deadline rather than to a tick
 * count — a backgrounded tab throttles `setInterval`, and a counter that only
 * decrements per tick would drift behind the server's clock, which is the one
 * that actually ends the set.
 *
 * `budgetSeconds` is the seconds LEFT when the payload was built (the session's
 * remaining time on resume, its full limit on a fresh start). Only null/absent
 * means UNTIMED — zero means the time is already gone, which is what a reload
 * after expiry looks like, and must land on the time-up screen rather than on a
 * quiz with no clock.
 */
export function useCountdown(budgetSeconds: number | null | undefined): Countdown | null {
  const [deadline, setDeadline] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (typeof budgetSeconds !== 'number' || !Number.isFinite(budgetSeconds)) {
      setDeadline(null);
      return;
    }
    const at = Date.now() + Math.max(0, budgetSeconds) * 1000;
    setDeadline(at);
    setNow(Date.now());
  }, [budgetSeconds]);

  useEffect(() => {
    if (deadline === null) return;
    const id = setInterval(() => {
      const tick = Date.now();
      setNow(tick);
      if (tick >= deadline) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  if (deadline === null) return null;
  const secondsRemaining = Math.max(0, Math.ceil((deadline - now) / 1000));
  return { secondsRemaining, expired: secondsRemaining === 0 };
}
